// ── Turning credit lots into the rows of the balance breakdown ──────────────
//
// The panel used to show three fixed rows — Plan credits, Purchased credits,
// Next to expire — against one aggregated balance. That could not answer the
// question people open it for: WHICH credits go, and WHEN. "Next to expire"
// named a number and a date but not what it belonged to, and the plan figure
// lumped together batches that lapse a month apart.
//
// The server now returns one bucket per (source, expiry day), so each batch can
// be its own row with its own date. This module decides how those rows are
// labelled, ordered and flagged — logic worth testing without mounting a page.

import { calendarDaysUntil } from './dates';

/**
 * How close to expiry a batch is called out, in days.
 *
 * Defined HERE rather than imported from notifications.js, which is where it
 * used to live. notifications.js now depends on this module (via
 * creditExpiryWarnings), so importing back the other way formed a cycle — and
 * the failure was silent rather than loud: the constant resolved to `undefined`
 * mid-initialisation, `days <= undefined` is false for every day, and EVERY
 * expiry warning quietly stopped appearing. Ownership belongs at the lower
 * level; notifications.js re-exports it for its existing callers.
 */
export const EXPIRING_SOON_DAYS = 14;

const isPlan = (b) => b?.source === 'plan';

/** 'Oct 19, 2026'. The house format, and UTC so a date-only value cannot slip a day. */
export function formatExpiry(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

/**
 * The rows to render, soonest-expiring first.
 *
 * Ordering is not cosmetic: credits are spent first-expiring-first, so this is
 * also the order they will actually be consumed. Showing them any other way
 * would misrepresent which balance is at risk.
 *
 * ── HOW PLAN ROWS ARE LABELLED ──────────────────────────────────────────────
 * Plan credits last two billing cycles, and the server now anchors every grant
 * within a cycle to that cycle's expiry — so a user holds at most two plan
 * batches, and the later-expiring one is necessarily this cycle's. That is what
 * makes "last cycle" / "this cycle" safe to say. Before the server change a
 * mid-cycle upgrade produced a third date and the labels would have lied.
 *
 * A single plan batch is labelled plainly, without a cycle: on a new account
 * there IS no previous cycle, and "this cycle" invites the reader to look for
 * a second row that does not exist.
 *
 * ── WHY A TRIAL OVERRIDES THE STORED EXPIRY ────────────────────────────────
 * On a free trial the plan lot IS the trial grant, and the date the server put
 * on it is a two-month default that never arrives. When the trial ends the
 * server ZEROES those lots (suspendPlanCredits, called from
 * reconcileTrialCredits) — so the credits are gone on day seven whatever
 * expires_at says.
 *
 * The card was therefore promising two months of use for credits with days
 * left: reading the stored date faithfully produced a false statement. Passing
 * the trial end in makes the row say what will actually happen. The stored
 * date would be better fixed at the grant, and that is worth doing on the
 * server, but it cannot be right to keep showing a date the product does not
 * honour while waiting for it.
 *
 * @param trialEndsOn  the trial end (YYYY-MM-DD or ISO) while a trial is
 *                     RUNNING, else null. Null is the ordinary subscription
 *                     case and changes nothing.
 * @param lots  [{ source: 'plan'|'purchased', expires_on: 'YYYY-MM-DD', remaining }]
 * @param now   injectable, so the expiring-soon boundary is testable
 */
export function buildBreakdownRows(lots, now = new Date(), { trialEndsOn = null } = {}) {
  if (!Array.isArray(lots) || lots.length === 0) return [];

  const clean = lots
    .filter((b) => b && Number(b.remaining) > 0)
    .map((b) => ({
      source: isPlan(b) ? 'plan' : 'purchased',
      // On a trial, the plan lot IS the trial grant and the date the server
      // stored on it never applies — see the note above buildBreakdownRows.
      expiresOn: (trialEndsOn && isPlan(b)) ? trialEndsOn : (b.expires_on ?? null),
      // Carried for the row KEY only — nothing here labels off it (the trial
      // label comes from trialEndsOn, which is an account-level fact). The
      // server buckets by (source, free/paid, day), so (source, day) alone is
      // not unique: a trial grant and a paid lot can share a day, and two rows
      // with one key is a list React cannot reconcile.
      isFree: !!b.is_free,
      remaining: Number(b.remaining) || 0,
    }));

  // The latest plan expiry is this cycle's; anything earlier is last cycle's.
  const planExpiries = clean.filter((b) => b.source === 'plan')
    .map((b) => b.expiresOn)
    .filter(Boolean)
    .sort();
  const latestPlanExpiry = planExpiries[planExpiries.length - 1] ?? null;
  const severalPlanBatches = new Set(planExpiries).size > 1;

  const rows = clean.map((b) => {
    const days = b.expiresOn == null ? null : calendarDaysUntil(b.expiresOn, now);
    return {
      key: `${b.source}${b.isFree ? ':free' : ''}:${b.expiresOn ?? 'none'}`,
      source: b.source,
      label: b.source === 'plan'
        ? planLabel(b.expiresOn, latestPlanExpiry, severalPlanBatches, trialEndsOn, b.isFree)
        : 'Purchased credits',
      remaining: b.remaining,
      expiresOn: b.expiresOn,
      expiresLabel: formatExpiry(b.expiresOn),
      daysUntilExpiry: days,
      // Null days means no date at all, which is not the same as "soon".
      // Negative would mean already lapsed; the server filters those out, but
      // the guard costs nothing and keeps a stale payload from reading as safe.
      expiringSoon: days != null && days >= 0 && days <= EXPIRING_SOON_DAYS,
    };
  });

  // Soonest first. A batch with no date sorts last — it cannot be urgent.
  return rows.sort((a, b) => {
    if (a.expiresOn == null) return 1;
    if (b.expiresOn == null) return -1;
    if (a.expiresOn !== b.expiresOn) return a.expiresOn < b.expiresOn ? -1 : 1;
    // Same day: plan before purchased, since plan is the one that lapses for
    // good rather than being replaced next cycle.
    return a.source === b.source ? 0 : (a.source === 'plan' ? -1 : 1);
  });
}

function planLabel(expiresOn, latestPlanExpiry, severalPlanBatches, trialEndsOn, isFree) {
  // A trial has one grant and no cycles, so neither 'Plan credits' nor the
  // this/last-cycle split describes it.
  if (trialEndsOn) return 'Free trial credits';
  // A FREE-plan grant on an account whose trial is over, or that never had
  // one. Buying a plan absorbs these into that plan's own balance server-side
  // (see refillPlanCredits), so a row still marked free is one that nothing
  // has been bought against — a credit pack deliberately leaves it alone,
  // neither extending its life nor making it purchased. Calling it "Plan
  // credits" alongside a "Free Plan" status line was the contradiction the
  // plugin's panel already names correctly.
  if (isFree) return 'Free credits';
  if (!severalPlanBatches) return 'Plan credits';
  return expiresOn === latestPlanExpiry
    ? 'Plan credits — this cycle'
    : 'Plan credits — last cycle';
}

/** The note under the rows. Both sentences answer a question the rows raise. */
export const BREAKDOWN_FOOTER =
  'Plan credits stay usable for two billing cycles. Credits expiring soonest are used first.';

/**
 * The same note, for an account still on its free trial.
 *
 * "Plan credits stay usable for two billing cycles" is a promise about a
 * subscription. A trial has no cycles and no second one to carry credits into —
 * the grant ends with the trial — so on that screen the sentence is simply
 * untrue, and it is untrue in the direction that costs the reader something:
 * it tells them they have two months to use credits that have days.
 *
 * REQUESTED: one sentence, not two — "Credits expiring soonest are used first"
 * is dropped from the trial note. It states when the trial credits stop and
 * nothing more.
 *
 * OLD:
 *   'Free trial credits are usable until your trial ends. Credits expiring soonest are used first.'
 */
export const TRIAL_BREAKDOWN_FOOTER = 'Free trial credits are usable until your trial ends.';

/**
 * How many KINDS of credit the rows hold — plan (on a trial, the trial grant)
 * and purchased. Counted from the built rows rather than the raw lots, so a
 * kind whose balance has run out, and therefore has no row, does not count.
 */
const creditTypeCount = (rows) =>
  new Set((Array.isArray(rows) ? rows : []).map((r) => r?.source)).size;

/**
 * Which note belongs under the rows — or null for none.
 *
 * ── ON A TRIAL, ONLY WHEN THERE IS SOMETHING TO TELL APART ──────────────────
 * REQUESTED: the trial note shows only when the breakdown holds two different
 * credit types. Beside a "Purchased credits" row the sentence says which of
 * the two runs out with the trial. Beside a lone "Free trial credits" row —
 * the usual trial account — that row already names itself and its end date,
 * so the note only repeats it; and with purchased credits alone (the trial
 * grant spent) it would describe a row that is not on screen.
 *
 * A subscriber's note is unchanged, whatever the rows hold.
 *
 * @param trialEndsOn  set only while a trial is RUNNING (see buildBreakdownRows)
 * @param rows         what buildBreakdownRows returned for the same lots
 *
 * OLD:
 *   export const breakdownFooter = (trialEndsOn) =>
 *     (trialEndsOn ? TRIAL_BREAKDOWN_FOOTER : BREAKDOWN_FOOTER);
 */
export const breakdownFooter = (trialEndsOn, rows = []) => {
  if (!trialEndsOn) return BREAKDOWN_FOOTER;
  return creditTypeCount(rows) > 1 ? TRIAL_BREAKDOWN_FOOTER : null;
};
